import { useEffect, useState } from 'react'

import {
  createChecklist,
  createChecklistItem,
  deleteChecklist,
  deleteChecklistItem,
  getChecklistItems,
  getRequestChecklists,
  updateChecklistItem,
} from '../../../../../api'
import { EMPTY_LIST } from '../../../../../constants/common'
import { useAsyncCallback } from '../../../../../hooks'
import { getItemsFromApiResponse } from '../../../../../utils/api'
import { REQUEST_PROFILE_TAB_INTERNAL_TASKS } from '../../../RequestProfile.constants'

import {
  addChecklistItem,
  getChecklistsWithItems,
  getChecklistWithItems,
  removeChecklistItem,
  replaceChecklistItem,
} from './useRequestProfileTabInternalTasksData.utils'

export function useRequestProfileTabInternalTasksData({
  requestId,
  onLoadCounters,
}) {
  const [checklists, setChecklists] = useState(EMPTY_LIST)

  const [isLoading, setLoading] = useState(true)

  const [hasError, setHasError] = useState(false)

  const handleReloadChecklists = useAsyncCallback(() => {
    return getRequestChecklists(requestId)
      .then(response => {
        const checklistObjects = getItemsFromApiResponse(response) || EMPTY_LIST

        return Promise.all(
          checklistObjects.map(checklist => getChecklistItems(checklist.id))
        ).then(itemsResponses => {
          setChecklists(
            getChecklistsWithItems(checklistObjects, itemsResponses)
          )
          setHasError(false)

          return true
        })
      })
      .catch(() => {
        setHasError(true)

        return null
      })
      .finally(() => {
        setLoading(false)
      })
  }, [requestId])

  useEffect(() => {
    if (requestId) {
      handleReloadChecklists()
    }
  }, [requestId])

  async function handleCreateChecklist({ name, template }) {
    return createChecklist({
      ...(template ? { template } : null),
      name,
      request: requestId,
    }).then(response =>
      // Template checklist items are copied on the backend, so they have to
      // be requested for the created checklist.
      (template ? getChecklistItems(response.data.id) : Promise.resolve()).then(
        itemsResponse => {
          setChecklists(prev => [
            ...prev,
            getChecklistWithItems(response.data, itemsResponse),
          ])
          onLoadCounters?.([REQUEST_PROFILE_TAB_INTERNAL_TASKS])

          return response
        }
      )
    )
  }

  async function handleDeleteChecklist(checklistId) {
    return deleteChecklist(checklistId).then(() => {
      setChecklists(prev =>
        prev.filter(checklist => checklist.id !== checklistId)
      )
      onLoadCounters?.([REQUEST_PROFILE_TAB_INTERNAL_TASKS])

      return true
    })
  }

  async function handleAddChecklistItem(checklistId, text) {
    return createChecklistItem({ checklist: checklistId, text }).then(
      response => {
        setChecklists(prev =>
          addChecklistItem(prev, checklistId, response.data)
        )
        onLoadCounters?.([REQUEST_PROFILE_TAB_INTERNAL_TASKS])

        return true
      }
    )
  }

  async function handleUpdateChecklistItem(checklistId, checklistItemId, data) {
    return updateChecklistItem(checklistItemId, data).then(response => {
      setChecklists(prev =>
        replaceChecklistItem(prev, checklistId, response.data)
      )

      return true
    })
  }

  async function handleDeleteChecklistItem(checklistId, checklistItemId) {
    return deleteChecklistItem(checklistItemId).then(() => {
      setChecklists(prev =>
        removeChecklistItem(prev, checklistId, checklistItemId)
      )

      return true
    })
  }

  return {
    checklists,
    handleAddChecklistItem,
    handleCreateChecklist,
    handleDeleteChecklist,
    handleDeleteChecklistItem,
    handleUpdateChecklistItem,
    hasError,
    isLoading,
  }
}
